import { Input } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import { useEffect, useRef, useState } from "react";
import { adminControlClass, adminLabelClass } from "../../../Shared/adminUi";

type FieldValue = string | number | File;

interface TypeFormProps<T> {
  onSubmit: (data: Record<string, FieldValue>) => void;
  onUpdate: (type: T) => void;
  editingType?: T;
  onCancelEdit: () => void;
  title?: string;
  fields?: Array<{
    name: string;
    label: string;
    type: "text" | "select" | "textarea" | "file";
    options?: Array<{ value: string | number; label: string }>;
    required?: boolean;
    accept?: string;
    getValue?: (type: T) => string | number;
  }>;
  storageBaseUrl?: string;
}

function TypeForm<T extends { id: number; name: string }>({
  onSubmit,
  onUpdate,
  editingType,
  onCancelEdit,
  title,
  fields = [],
  storageBaseUrl,
}: TypeFormProps<T>) {
  const [formData, setFormData] = useState<Record<string, FieldValue>>({
    name: "",
  });
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (editingType) {
      const data: Record<string, FieldValue> = { name: editingType.name };
      fields.forEach((field) => {
        if (field.type === "file") {
          const existing = editingType[field.name as keyof T];
          if (typeof existing === "string" && existing) {
            data[field.name] = existing;
          }
        } else if (field.getValue) {
          data[field.name] = field.getValue(editingType);
        } else if (field.name in editingType) {
          const value = editingType[field.name as keyof T];
          if (typeof value === "string" || typeof value === "number") {
            data[field.name] = value;
          }
        }
      });
      setFormData(data);
    } else {
      const data: Record<string, FieldValue> = { name: "" };
      fields.forEach((field) => {
        if (field.type === "file") {
          const input = fileInputRefs.current[field.name];
          if (input) input.value = "";
        } else if (field.options && field.options.length > 0) {
          data[field.name] = field.options[0].value;
        }
      });
      setFormData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const nameValue = String(formData.name || "");
    if (!nameValue.trim()) return;

    if (editingType) {
      const updatedType = {
        ...editingType,
        ...formData,
        name: nameValue.trim(),
      };
      onUpdate(updatedType);
    } else {
      const submitData: Record<string, FieldValue> = {
        ...formData,
        name: nameValue.trim(),
      };
      fields.forEach((field) => {
        if (field.type === "file") {
          const fromState = formData[field.name];
          const file =
            fromState instanceof File
              ? fromState
              : fileInputRefs.current[field.name]?.files?.[0];
          if (file) submitData[field.name] = file;
        }
      });
      onSubmit(submitData);

      const resetData: Record<string, FieldValue> = { name: "" };
      fields.forEach((field) => {
        if (field.type === "file") {
          const input = fileInputRefs.current[field.name];
          if (input) input.value = "";
        } else if (field.options && field.options.length > 0) {
          resetData[field.name] = field.options[0].value;
        }
      });
      setFormData(resetData);
    }
  };

  const handleFieldChange = (fieldName: string, value: FieldValue) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <form className="flex flex-col" onSubmit={handleSubmit}>
      <h4 className="text-ink mb-4 text-center font-semibold">
        {editingType ? "Redaguoti tipą:" : title || "Pridėti naują tipą:"}
      </h4>
      <Input
        label="Pavadinimas"
        type="text"
        className="mb-4"
        value={String(formData.name || "")}
        onChange={(event) => handleFieldChange("name", event.target.value)}
        required
      />

      {fields.map((field) => {
        if (field.type === "select" && field.options) {
          return (
            <div key={field.name} className="mb-4">
              <label className={adminLabelClass}>{field.label}</label>
              <select
                className={adminControlClass}
                value={String(
                  formData[field.name] ?? field.options[0]?.value ?? "",
                )}
                onChange={(event) =>
                  handleFieldChange(
                    field.name,
                    typeof field.options![0].value === "number"
                      ? Number(event.target.value)
                      : event.target.value,
                  )
                }
                required={field.required}
              >
                {field.options.map((option) => (
                  <option key={String(option.value)} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        if (field.type === "textarea") {
          return (
            <div key={field.name} className="mb-4">
              <label className={adminLabelClass}>{field.label}</label>
              <textarea
                className={adminControlClass}
                rows={4}
                value={String(formData[field.name] ?? "")}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                required={field.required}
              />
            </div>
          );
        }
        if (field.type === "file") {
          const currentValue = formData[field.name];
          const existingPath =
            typeof currentValue === "string" ? currentValue : null;
          
          const previewUrl = existingPath
            ? existingPath.startsWith('http')
              ? existingPath
              : storageBaseUrl
                ? `${storageBaseUrl}/${existingPath}`
                : null
            : null;
          
          return (
            <div key={field.name} className="mb-4">
              <label className={adminLabelClass}>{field.label}</label>
              <div className="flex items-center gap-4">
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="border-line-strong h-16 w-16 shrink-0 rounded-lg border object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <input
                  ref={(el) => {
                    fileInputRefs.current[field.name] = el;
                  }}
                  type="file"
                  accept={field.accept ?? "image/*"}
                  className={`${adminControlClass} min-w-0 text-sm file:mr-2 file:rounded-lg file:border-0 file:bg-panel file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFieldChange(field.name, file);
                  }}
                  required={field.required && !existingPath}
                />
              </div>
            </div>
          );
        }
        return (
          <Input
            key={field.name}
            label={field.label}
            type={field.type}
            className="mb-4"
            value={String(formData[field.name] ?? "")}
            onChange={(event) =>
              handleFieldChange(field.name, event.target.value)
            }
            required={field.required}
          />
        );
      })}

      <Button type="submit" className="mt-4" size="sm">
        {editingType ? "Išsaugoti pakeitimus" : "Išsaugoti"}
      </Button>

      {editingType && (
        <Button
          variant="secondary"
          type="button"
          className="mt-2"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCancelEdit();
          }}
        >
          Atšaukti redagavimą
        </Button>
      )}
    </form>
  );
}

export default TypeForm;
